import Vue from 'vue';
import Router from 'vue-router';
import Home from '@/views/Home/index';
import Login from '@/views/Login/index';
import Settings from '@/views/Settings/index';
import Logout from '@/views/Logout/index';
import requireApplication from './requireApplication';
import requirePerm from './requirePerm';
import requireRole from './requireRole';

Vue.use(Router);

/**
 * A constructor for our router. It loads all default views at their default routes. It assumes that all view
 * components will be available by their names under @/views/.
 *
 * @param {Object} options - an object specifying any additional configs/views you would like to add
 */
export default function createRouter(options) {
	// Configure how we want our router
	const config = {
		mode: 'history',
		routes: [
			{
				path: '/',
				name: 'home',
				component: Home
			},
			{
				path: '/login',
				name: 'login',
				component: Login
			},
			{
				path: '/settings',
				name: 'settings',
				component: Settings
			},
			{
				path: '/logout',
				name: 'logout',
				component: Logout
			},
			{
				path: '/reset-password',
				name: 'resetPassword',
				component: () => import(/* webpackChunkName: "About" */ '@/views/ResetPassword/index')
			},
			{
				path: '/register',
				name: 'register',
				component: () => import(/* webpackChunkName: "About" */ '@/views/Register/index')
			},
			{
				path: '/contact',
				name: 'contact',
				component: () => import(/* webpackChunkName: "Contact" */ '@/views/Contact/index')
			},
			{
				path: '/products',
				name: 'products',
				component: () => import(/* webpackChunkName: "Products" */ '@/views/Products/index'),
				beforeEnter: requirePerm('view products') // Make sure only authorized people can shop
			},
			{
				path: '/application',
				name: 'application',
				component: () => import(/* webpackChunkName: "Application" */ '@/views/Application/index'),
				beforeEnter: requireRole('preApplication') // Make sure only preApplication users can go here
			},
			{
				path: '/checkout',
				name: 'checkout',
				component: () => import(/* webpackChunkName: "Checkout" */ '@/views/Checkout/index'),
				beforeEnter: requireApplication() // Make sure only authorized people can checkout
			},
			{
				path: '/account',
				component: () => import(/* webpackChunkName: "Account" */ '@/views/Account/index'),
				children: [
					{
						path: 'company',
						name: 'account.company',
						component: () => import(/* webpackChunkName: "Company" */ '@/views/Account/Company/index'),
						beforeEnter: requirePerm('view company') // Make sure only authorized people can view ocompany information
					},
					{
						path: 'stores',
						component: () => import(/* webpackChunkName: "Stores" */ '@/views/Account/Stores/index'),
						children: [
							{
								path: ':storeId',
								name: 'account.stores.storeDetails',
								component: () => import(/* webpackChunkName: "StoreDetails" */ '@/views/Account/Stores/StoreDetails/index'),
								beforeEnter: requirePerm('view stores') // Make sure only authorized people can view store details
							},
							{
								path: '', // Will match and be rendered if there is no nested path given, i.e. "/account/stores"
								name: 'account.stores',
								component: () => import(/* webpackChunkName: "StoresOverview" */ '@/views/Account/Stores/StoresOverview/index'),
								beforeEnter: requirePerm('view stores') // Make sure only authorized people can view store details
							}
						]
					},
					{
						path: 'orders',
						component: () => import(/* webpackChunkName: "Orders" */ '@/views/Account/Orders/index'),
						children: [
							{
								path: ':orderId',
								name: 'account.orders.orderDetails',
								component: () => import(/* webpackChunkName: "OrderDetails" */ '@/views/Account/Orders/OrderDetails/index'),
								beforeEnter: requirePerm('view order details') // Make sure only authorized people can view order details
							},
							{
								path: '', // Will match and be rendered if there's no nested path given, i.e. "/account/orders"
								name: 'account.orders',
								component: () => import(/* webpackChunkName: "OrdersOverview" */ '@/views/Account/Orders/OrdersOverview/index'),
								beforeEnter: requirePerm('view order history')
							}
						]
					},
					{
						path: 'users',
						component: () => import(/* webpackChunkName: "Users" */ '@/views/Account/Users/index'),
						children: [
							{
								path: ':userId',
								name: 'account.users.userDetails',
								component: () => import(/* webpackChunkName: "UserDetails" */ '@/views/Account/Users/UserDetails/index'),
								beforeEnter: requirePerm('view users') // Make sure only authorized people can view user details
							},
							{
								path: '',
								name: 'account.users',
								component: () => import(/* webpackChunkName: "Users" */ '@/views/Account/Users/UsersOverview/index'),
								beforeEnter: requirePerm('view users') // Make sure only authorized people can view users
							}
						]
					}
				]
			},
			{
				path: '/admin',
				component: () => import(/* webpackChunkName: "Admin" */ '@/views/Admin/index'),
				children: [
					{
						path: 'applications',
						component: () => import(/* webpackChunkName: "Applications" */ '@/views/Admin/Applications/index'),
						children: [
							{
								path: ':appId',
								name: 'admin.applications.applicationDetails',
								component: () =>
									import(/* webpackChunkName: "ApplicationDetails" */ '@/views/Admin/Applications/ApplicationDetails/index'),
								beforeEnter: requireRole('admin') // Make sure only admins can go here
							},
							{
								path: '', // Will match and be rendered if there is no nested path given, i.e. "/admin/applications"
								name: 'admin.applications',
								component: () =>
									import(/* webpackChunkName: "ApplicationsOverview" */ '@/views/Admin/Applications/ApplicationsOverview/index'),
								beforeEnter: requireRole('admin') // Make sure only admins can go here
							}
						]
					},
					{
						path: 'users',
						component: () => import(/* webpackChunkName: "AdminUsers" */ '@/views/Admin/Users/index'),
						children: [
							{
								path: '',
								name: 'admin.users',
								component: () => import(/* webpackChunkName: "AdminUsersOverview" */ '@/views/Admin/Users/UsersOverview/index'),
								beforeEnter: requireRole('admin') // Make sure only authorized people can view users
							}
						]
					},
					{
						path: 'system',
						name: 'admin.system',
						component: () => import(/* webpackChunkName: "AdminSystem" */ '@/views/Admin/System/index'),
						beforeEnter: requireRole('admin') // Make sure only authorized people can view users
					}
				]
			}
		],
		// Scroll the user back to the top of the page on any route change
		// https://router.vuejs.org/guide/advanced/scroll-behavior.html#scroll-behavior
		scrollBehavior() {
			return { x: 0, y: 0 };
		}
	};

	// In case other, additional routes and components were passed in, here we include them
	if (options.views && options.views.length) {
		for (const view of options.views) {
			// All additional views passed in must match the syntax of those seen above, and not clash with their paths
			if (config.routes.find(route => route.path === view.path)) {
				throw new Error(`Cannot configure additional view at route ${view.path}. That path is already taken.`);
			}

			config.routes.push(view);
		}
	}

	// Finally, we need to make sure that our 404/catch-all view is the last one in the list
	config.routes.push({
		path: '*',
		name: '404',
		component: () => import(/* webpackChunkName: "404" */ '@/views/404/index')
	});

	return new Router(config);
}
